import React, {useMemo} from "react";
import {
  Page,
  Text,
  View,
  Document as PdfDocument,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";

import {
  calculatePdfFontSize,
  calculatePdfHeight,
  calculatePdfWidth,
  getCuttingBorders,
  getPageOrientation,
  getPriceFromNumber,
  labelsNumberOnPage
} from "../../../Utils";
import {FormPaperColorEnum, FormPrintColorEnum, FormPrintOptionsType} from "../../../Types";
import {FormLabelType} from "../Types";
{/* @ts-ignore */}
import BebasNeueRegular from "../../../Fonts/BebasNeue-Regular.ttf";
{/* @ts-ignore */}
import ArimoItalic from "../../../Fonts/Arimo-Italic.ttf";

import * as Icons from "../../../Icons/Components";

Font.register({ family: 'BebasNeueRegular', src: BebasNeueRegular });
Font.register({ family: 'ArimoItalic', src: ArimoItalic, fontStyle: 'italic' });

// No word wrap, see: https://v2.react-pdf.org/advanced#hyphenation
const hyphenationCallback = (word: any) => {
  return [word];
}
Font.registerHyphenationCallback(hyphenationCallback);

export const Pdf = ({printOptions, labels}: { printOptions: FormPrintOptionsType, labels: FormLabelType[] }) => {
  const logo = printOptions.paperColor === FormPaperColorEnum.YELLOW ? "logo-min-supra-couleur.png" : "logo-min-supra-nb.png"
  const pageBackgroundColor = printOptions.paperColor === FormPaperColorEnum.WHITE ? "white" : "#F8D96E"
  const colorRed = printOptions.printColor === FormPrintColorEnum.BW ? "black" : "red"

  const styles = StyleSheet.create({
    page: {
      flexDirection: "row",
      flexWrap: "wrap",
      backgroundColor: pageBackgroundColor,
      margin: 0,
      padding: 0,
    },
    label: {
    },
    titleContainer: {
      flexDirection: 'row',
      borderColor: "black",
      borderBottom: calculatePdfHeight(7, printOptions.size),
      height: calculatePdfHeight(170, printOptions.size),
      marginBottom: calculatePdfHeight(30, printOptions.size),
    },
    title: {
      fontFamily: 'BebasNeueRegular',
      maxLines: 1,
      color: colorRed,
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      width: '100%',
      fontSize: calculatePdfFontSize(110, printOptions.size),
      lineHeight: calculatePdfFontSize(110, printOptions.size) + "px",
    },
    name: {
      fontFamily: 'BebasNeueRegular',
      maxLines: 1,
      fontSize: calculatePdfFontSize(65, printOptions.size),
      lineHeight: calculatePdfFontSize(65, printOptions.size) + "px",
    },
    capacity: {
      fontFamily: 'ArimoItalic',
      fontStyle: "italic",
      fontWeight: "normal",
      maxLines: 1,
      fontSize: calculatePdfFontSize(25, printOptions.size),
    },
    discountBox: {
      flexDirection: 'row',
      borderColor: colorRed,
      backgroundColor: colorRed,
      fontFamily: 'BebasNeueRegular',
      borderWidth: calculatePdfWidth(8, printOptions.size),
    },
    discount: {
      justifyContent: 'center',
      alignItems: 'center',
      color: pageBackgroundColor,
      textAlign: 'center',
      overflow: 'hidden',
      paddingTop: calculatePdfHeight(20, printOptions.size),
      paddingBottom: calculatePdfHeight(20, printOptions.size),
      paddingLeft: calculatePdfWidth(20, printOptions.size),
      paddingRight: calculatePdfWidth(20, printOptions.size),
    },
    priceBox: {
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: pageBackgroundColor,
      flexGrow: 1,
      overflow: 'hidden',
    },
    oldPrice: {
      textDecoration: 'line-through',
      opacity: 0.5,
      fontSize: calculatePdfFontSize(36, printOptions.size),
    },
    price: {
      color: colorRed,
      textAlign: 'center',
    },
    priceEuroRaw: {
      display: "flex",
      flexDirection: "row",
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      color: colorRed,
      textAlign: "center",
    },
    priceEuroWhole: {},
    priceEuroDecimalRaw: {
      display: "flex",
      flexDirection: "column",
      paddingLeft: calculatePdfWidth(20, printOptions.size),
    },
    priceEuroDecimalSymbole: {
    },
    priceEuroDecimal: {
    },
    footer: {
      position: "absolute",
      bottom: 0,
      width: "100%",
    },
    footerLegalNotice: {
      width: "100%",
      textAlign: "center",
      color: "black",
      fontFamily: "ArimoItalic",
      fontStyle: "italic",
      fontSize: calculatePdfFontSize(18, printOptions.size),
      paddingBottom: calculatePdfHeight(5, printOptions.size),
      maxLines: 1,
      paddingLeft: calculatePdfWidth(15, printOptions.size) + "px",
      paddingRight: calculatePdfWidth(15, printOptions.size) + "px",
    },
    footerBaseLine: {
      backgroundColor: "black",
      textAlign: "center",
      color: pageBackgroundColor,
      fontFamily: "BebasNeueRegular",
      fontSize: calculatePdfFontSize(14, printOptions.size),
      width: "100%",
      paddingTop: calculatePdfHeight(7, printOptions.size),
      paddingBottom: calculatePdfHeight(7, printOptions.size),
    },
    footerLogo: {
      position: "absolute",
      top: calculatePdfHeight(5, printOptions.size),
      left: calculatePdfWidth(70, printOptions.size),
      height: calculatePdfHeight(25, printOptions.size),
    }
  });

  const themeLabelBySize = useMemo(() => ({
    A4: {
      portrait: {
        height: "29.7cm",
        width: "21cm",
      },
      landscape: {
        height: "21cm",
        width: "29.7cm",
      },
    },
    A5: {
      portrait: {
        height: "21cm",
        width: "14.85cm",
      },
      landscape: {
        height: "14.85cm",
        width: "21cm",
      },
    },
    A6: {
      portrait: {
        height: "14.85cm",
        width: "10.5cm",
      },
      landscape: {
        height: "10.5cm",
        width: "14.85cm",
      },
    },
    A7: {
      portrait: {
        height: "10.5cm",
        width: "7.425cm",
      },
      landscape: {
        height: "7.425cm",
        width: "10.5cm",
      },
    },
  }), []);

  const themeNameByOrientation = useMemo(() => ({
    landscape: {
      fontSize: calculatePdfFontSize(65, printOptions.size),
      lineHeight: calculatePdfFontSize(65, printOptions.size) + "px",
    },
    portrait: {
      fontSize: calculatePdfFontSize(50, printOptions.size),
      lineHeight: calculatePdfFontSize(50, printOptions.size) + "px",
    },
  }), [printOptions.size]);

  const themeCapacityByOrientation = useMemo(() => ({
    landscape: {
      marginTop: calculatePdfHeight(2, printOptions.size),
    },
    portrait: {
      marginTop: calculatePdfHeight(10, printOptions.size),
    }
  }), [printOptions.size]);

  const themeDiscountByOrientation = useMemo(() => ({
    landscape: {
      maxWidth: calculatePdfWidth(245, printOptions.size),
      minWidth: calculatePdfWidth(245, printOptions.size),
      maxHeight: calculatePdfHeight(320, printOptions.size),
      minHeight: calculatePdfHeight(320, printOptions.size),
    },
    portrait: {
      maxWidth: calculatePdfWidth(220, printOptions.size),
      minWidth: calculatePdfWidth(220, printOptions.size),
      maxHeight: calculatePdfHeight(350, printOptions.size),
      minHeight: calculatePdfHeight(350, printOptions.size),
    }
  }), [printOptions.size]);

  const themeDiscountBoxByOrientation = useMemo(() => ({
    landscape: {
      marginTop: calculatePdfHeight(40, printOptions.size),
      maxHeight: themeDiscountByOrientation.landscape.maxHeight + (2 * styles.discount.paddingTop) - (calculatePdfHeight(8, printOptions.size) * 3),
      minHeight: themeDiscountByOrientation.landscape.minHeight + (2 * styles.discount.paddingTop) - (calculatePdfHeight(8, printOptions.size) * 3),
    },
    portrait: {
      marginTop: calculatePdfHeight(60, printOptions.size),
      maxHeight: themeDiscountByOrientation.portrait.maxHeight + (2 * styles.discount.paddingTop) - (calculatePdfHeight(8, printOptions.size) * 3),
      minHeight: themeDiscountByOrientation.portrait.minHeight + (2 * styles.discount.paddingTop) - (calculatePdfHeight(8, printOptions.size) * 3),
    },
  }), [themeDiscountByOrientation, styles, printOptions.size, styles.discount.paddingTop]);

  const themePriceByOrientation = useMemo(() => ({
    landscape: {
      maxWidth: calculatePdfWidth(517, printOptions.size),
    },
    portrait: {
      maxWidth: calculatePdfWidth(315, printOptions.size),
    },
  }), [printOptions.size]);

  const priceEuroRawByOrientation = useMemo(() => ({
    landscape: {
      marginTop: calculatePdfHeight(-40, printOptions.size),
      marginBottom: calculatePdfHeight(-10, printOptions.size),
    },
    portrait: {
      marginTop: calculatePdfHeight(-10, printOptions.size),
      marginBottom: calculatePdfHeight(-10, printOptions.size),
    },
  }), [printOptions.size]);

  const priceEuroWholeByOrientation = useMemo(() => ({
    landscape: {
      fontSize: calculatePdfFontSize(300, printOptions.size),
      lineHeight: calculatePdfFontSize(300, printOptions.size) + "px",
    },
    portrait: {
      fontSize: calculatePdfFontSize(230, printOptions.size),
      lineHeight: calculatePdfFontSize(230, printOptions.size) + "px",
    },
  }), [printOptions.size]);

  const priceEuroDecimalSymboleByOrientation = useMemo(() => ({
    landscape: {
      fontSize: calculatePdfFontSize(100, printOptions.size),
      lineHeight: calculatePdfFontSize(100, printOptions.size) + "px",
      marginTop: calculatePdfHeight(30, printOptions.size),
    },
    portrait: {
      fontSize: calculatePdfFontSize(75, printOptions.size),
      lineHeight: calculatePdfFontSize(75, printOptions.size) + "px",
      marginTop: calculatePdfHeight(15, printOptions.size),
    },
  }), [printOptions.size]);

  const priceEuroDecimalByOrientation = useMemo(() => ({
    landscape: {
      fontSize: calculatePdfFontSize(100, printOptions.size),
      lineHeight: calculatePdfFontSize(100, printOptions.size) + "px",
      marginTop: calculatePdfHeight(30, printOptions.size),

    },
    portrait: {
      fontSize: calculatePdfFontSize(75, printOptions.size),
      lineHeight: calculatePdfFontSize(75, printOptions.size) + "px",
      marginTop: calculatePdfHeight(35, printOptions.size),
    },
  }), [printOptions.size]);

  const groupedLabels = [];
  for (let i = 0; i < labels.length; i += labelsNumberOnPage(printOptions.size)) {
    groupedLabels.push(labels.slice(i, i + labelsNumberOnPage(printOptions.size)));
  }

  // @ts-ignore
  return (
    <PdfDocument>
      {groupedLabels.map((labels, pageIndex) => (
        <Page
          orientation={getPageOrientation(printOptions.size, printOptions.orientation)}
          size={"A4"}
          style={{...styles.page}}
          key={'page' + pageIndex}
        >
          {labels.map((label, labelIndex) => {
            // @ts-ignore
            const Icon = Icons[label.icon.replace(/[^a-zA-Z]/g, '')] || null;
            const labelBorders = getCuttingBorders(printOptions.size, printOptions.orientation, labelIndex);

            let priceEuro = label.price && label.isPriceEuro
              ? getPriceFromNumber(parseFloat(label.price.replace(',', '.')))
              : null;

            return (
              // @ts-ignore
              <View key={'label' + label.id}>
                {/* @ts-ignore */}
                <View style={{...styles.label, ...themeLabelBySize[printOptions.size][printOptions.orientation], ...labelBorders}}>
                  {/* TITLE */}
                  {printOptions.orientation === 'portrait' && (
                    /* @ts-ignore */
                    <View style={{...styles.titleContainer}}>
                      {/* @ts-ignore */}
                      <View style={{...styles.title}}>
                        <Text>{label.title.length > 0 ? label.title : " "}</Text>
                      </View>
                    </View>
                  )}

                  <View style={{padding: calculatePdfHeight(15, printOptions.size) + "px" + " " + calculatePdfWidth(15, printOptions.size) + "px"}}>
                    {/* NAME & CAPACITY */}
                    <View>
                      {/* @ts-ignore */}
                      <Text style={{...styles.name, ...themeNameByOrientation[printOptions.orientation]}}>
                        {label.name.length > 0 ? label.name : " "}
                      </Text>
                      {/* @ts-ignore */}
                      <Text style={{...styles.capacity, ...themeCapacityByOrientation[printOptions.orientation]}}>
                        {label.capacity.length > 0 ? label.capacity : " "}
                      </Text>
                    </View>

                    {/* DISCOUNT */}
                    {/* @ts-ignore */}
                    <View style={{...styles.discountBox, ...themeDiscountBoxByOrientation[printOptions.orientation]}}>
                      {/* @ts-ignore */}
                      <View style={{...styles.discount, ...themeDiscountByOrientation[printOptions.orientation], fontSize: calculatePdfFontSize(label.discountSize, printOptions.size), lineHeight: calculatePdfFontSize(label.discountSize, printOptions.size) + "px"}}>
                        {Icon ? <Icon color={pageBackgroundColor} /> : label.icon ? <Text>{label.discount}</Text> : null}
                      </View>
                      <View style={styles.priceBox}>
                        {/* @ts-ignore */}
                        <View style={{...styles.price, ...themePriceByOrientation[printOptions.orientation], fontSize: calculatePdfFontSize(label.priceSize, printOptions.size), lineHeight: calculatePdfFontSize(label.priceSize, printOptions.size) + "px"}}>
                          {priceEuro ? (
                            <View style={{...styles.priceEuroRaw, ...priceEuroRawByOrientation[printOptions.orientation]}}>
                              <Text style={{...styles.priceEuroWhole, ...priceEuroWholeByOrientation[printOptions.orientation]}}>{priceEuro.whole}</Text>

                              <View style={styles.priceEuroDecimalRaw}>
                                <Text style={{...styles.priceEuroDecimalSymbole, ...priceEuroDecimalSymboleByOrientation[printOptions.orientation]}}>
                                  €
                                </Text>
                                <Text style={{...styles.priceEuroDecimal, ...priceEuroDecimalByOrientation[printOptions.orientation]}}>
                                  ,{priceEuro.decimals}
                                </Text>
                              </View>
                            </View>
                          ) : <Text>{label.price ?? ' '}</Text>}
                        </View>
                        {/* @ts-ignore */}
                        <Text style={{...styles.oldPrice}}>{label.oldPrice && label.oldPrice + '€'}</Text>
                      </View>
                    </View>
                  </View>

                  {/* FOOTER */}
                  <View style={styles.footer}>
                    <View style={styles.footerLegalNotice}>
                      <Text>
                        Offre valable dans la limite des stocks disponibles
                        {label.discountDetail.length > 0 ? ", " + label.discountDetail : ""}
                        .
                      </Text>
                    </View>

                    <View style={styles.footerBaseLine}>
                      <Image src={logo} style={styles.footerLogo}/>

                      <Text>SUPRAPHARM - SANTEZ VOUS MIEUX</Text>
                    </View>
                  </View>
                </View>
              </View>
            )
          })}
        </Page>
      ))}
    </PdfDocument>
  );
};

export default Pdf;
